import React, { useState, useEffect, useRef } from 'react';
import { Toast } from '../../components/common/Toast';
import ChooseTemplate from './components/ChooseTemplate';
import TextEditor from './components/Texteditor';
import MainPageLayout from '../../components/common/MainPageLayout';
import { SaveButtonIcon } from '../../components/common/SvgIcon';
import FormCard from '../../components/common/FormCard';

const AddNewPage = () => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [freeTemplates, setFreeTemplates] = useState([]);
  const [proTemplates, setProTemplates] = useState([]);
  const [shortcodes, setShortcodes] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedPage, setSavedPage] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null); 

  const isProActive = LEGAL_PAGES?.license?.status === 'valid';

  useEffect(() => {
    fetchTemplates();
    fetchShortcodes();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${LEGAL_PAGES.apiUrl}/templates?simple=true`, {
        headers: {
          'X-WP-Nonce': LEGAL_PAGES.nonce,
        },
      });

      const result = await response.json();

      if (result.success) {
        setFreeTemplates(result.data.free || []);
        setProTemplates(result.data.pro || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      Toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchShortcodes = async () => {
    try {
      const response = await fetch(`${LEGAL_PAGES.apiUrl}/shortcodes`, {
        headers: {
          'X-WP-Nonce': LEGAL_PAGES.nonce,
        },
      });

      const result = await response.json();

      if (result.success) {
        setShortcodes(result.data || {});
      }
    } catch (error) {
      console.error('Error fetching shortcodes:', error);
    }
  };

  const handleTemplateClick = async (templateId, templateType) => {
    if (templateType === 'pro' && !isProActive) {
      Toast.warning('Please upgrade to PRO to use this template');
      return;
    }

    try {
      const response = await fetch(`${LEGAL_PAGES.apiUrl}/templates/${templateId}`, {
        headers: {
          'X-WP-Nonce': LEGAL_PAGES.nonce,
        },
      });

      const result = await response.json();

      if (result.success) {
        setTitle(result.data.name);
        setContent(result.data.content);
        setSelectedTemplateId(templateId);
        setSavedPage(null);
        Toast.success('Template loaded successfully!');
      }
    } catch (error) {
      console.error('Error loading template:', error);
      Toast.error('Failed to load template');
    }
  };

  const handleEditorChange = (newContent) => {
    setContent(newContent);
  };

  const handleMediaUpload = () => {
    // Check if wp.media is available
    if (typeof wp === 'undefined' || typeof wp.media === 'undefined') {
      console.error('WordPress media library is not available');
      alert('Media library is not available. Please refresh the page.');
      return;
    }

    const frame = wp.media({
      title: 'Select Image',
      button: {
        text: 'Use selected image',
      },
      multiple: false,
    });

    frame.on('select', () => {
      const attachment = frame.state().get('selection').first().toJSON();
      const imgHtml = `<img src="${attachment.url}" alt="${attachment.alt || attachment.title}" style="max-width:100%;">`;

      const editor = window.tinymce.get('legal-pages-classic-editor');
      if (editor) {
        editor.insertContent(imgHtml);
      }
    });

    frame.open();
  };

  const handleSave = async () => {
    if (!title.trim()) {
      Toast.error('Please enter a page title');
      return;
    }

    if (!content.trim()) {
      Toast.error('Please add content');
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(`${LEGAL_PAGES.apiUrl}/legal-page`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': LEGAL_PAGES.nonce,
        },
        body: JSON.stringify({
          title,
          content,
        }),
      });

      const result = await response.json();

      if (result.success) {
        Toast.success('Legal page created successfully!');
        setSavedPage(result.data);
        setTitle('');
        setContent('');
      } else {
        Toast.error('Failed to save legal page');
      }
    } catch (error) {
      console.error('Error saving page:', error);
      Toast.error('An error occurred while saving');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    window.location.hash = '/all-legal-pages';
  };

  return (
    <MainPageLayout
        title="Add New Legal Page"
        description="Create a new legal page using one of our templates"
      >
      {/* Back Button */}
      <div className="mb-6">
        <button
          onClick={handleCancel}
          className="inline-flex items-center gap-2 px-4 py-2 text-[#6F6F7B] hover:text-[#17171C] transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Legal Pages
        </button>
      </div>

      {/* {savedPage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <h3 className="font-semibold text-green-900 mb-1">Page Created Successfully!</h3>
              <p className="text-sm text-green-700 mb-3">"{savedPage.title}" has been published.</p>
              <div className="flex gap-2">
                <a
                  href={savedPage.view_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 transition"
                >
                  View Page
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <a
                  href={savedPage.edit_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-4 py-2 bg-white border border-green-600 text-green-600 rounded-xl text-sm font-medium hover:bg-green-50 transition"
                >
                  Edit Page
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </a>
              </div>
            </div>
            <button
              onClick={() => setSavedPage(null)}
              className="text-green-400 hover:text-green-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )} */}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <FormCard
            as="input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter page title..."
            className="!py-10 !px-6 !text-lg text-[#17171C] outline-none focus:ring-2 focus:ring-[#17171C]/5 focus:border-[#17171C]"
          />

          <FormCard className="mt-6 overflow-hidden">
            <div className="p-2">
              <TextEditor
                content={content}
                onChange={handleEditorChange}
                handleMediaUpload={handleMediaUpload}
              />
            </div>
          </FormCard>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className={`
                inline-flex items-center gap-2 px-8 py-[14px] cursor-pointer rounded-xl font-semibold text-white transition shadow-[0px_4px_14px_-2px_#7C3BED40]
                ${saving ? 'bg-indigo-400 cursor-not-allowed' : 'bg-lgp-color'}
              `}
            >
              <SaveButtonIcon />
              {saving ? 'Saving...' : 'Save Page'}
            </button>

            <button
              onClick={handleCancel}
              disabled={saving}
              className="px-6 py-[14px] cursor-pointer border border-[#E4E4E7] text-[#6F6F7B] rounded-xl font-semibold hover:border-[#17171C] hover:text-[#17171C] transition-all disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>  

        <ChooseTemplate
          freeTemplates={freeTemplates}
          proTemplates={proTemplates}
          shortcodes={shortcodes}
          handleTemplateClick={handleTemplateClick}
          isProActive={isProActive}
          loading={loading}
          selectedTemplateId={selectedTemplateId}
        />
      </div>
    </MainPageLayout>
  );
};

export default AddNewPage;